import type { FileEntry } from '@/lib/api/generated/model';
import { isInsideFolder, isProtectedFolder } from './folders';

/**
 * Somewhere an item can be dropped: a folder row, or the `..` row.
 *
 * The `..` row carries a path rather than an entry because it is not an entry —
 * it stands for the folder the reader is currently inside (research R4).
 */
export type DropTarget =
  { kind: 'folder'; path: string } | { kind: 'parent'; path: string };

/**
 * Where `entry` would land in `target`.
 *
 * The name is preserved; only the containing folder changes. The workspace root
 * is the empty path, which is why the separator is conditional — `/notes.txt`
 * would be an absolute path the service rejects.
 */
export function destinationFor(entry: FileEntry, target: DropTarget): string {
  return target.path === '' ? entry.name : `${target.path}/${entry.name}`;
}

/**
 * Files and folders alike can be moved — except a folder the product needs to
 * keep in place (`protectionReason`: `.agents`, `tasks`, a task's folder).
 */
export function canDrag(entry: FileEntry): boolean {
  if (isProtectedFolder(entry)) return false;
  return entry.kind === 'file' || entry.kind === 'directory';
}

/**
 * Whether dropping `entry` on `target` is a move that could succeed.
 *
 * Answered here, before the drop, so an impossible destination is shown as
 * refusing rather than failing afterwards (FR-030). Four rules:
 *
 * 1. A folder cannot be dropped into itself.
 * 2. A folder cannot be dropped into its own descendants — checked with the
 *    same whole-segment rule the service uses, so `docs` → `docs-archive` stays
 *    legal while `docs` → `docs/sub` does not.
 * 3. An item already directly inside the target is not going anywhere.
 * 4. Only folders and the parent row accept a drop; file rows never do, which
 *    is enforced by `DropTarget` having no case for them.
 */
export function canDrop(entry: FileEntry, target: DropTarget): boolean {
  // A protected folder goes nowhere, however it was picked up (spec 120 FR-010).
  if (isProtectedFolder(entry)) return false;
  if (entry.path === target.path) return false;
  if (isInsideFolder(target.path, entry.path)) return false;

  return destinationFor(entry, target) !== entry.path;
}
